import React, { useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { InformationCircleIcon } from '@heroicons/react/24/outline';
import { Button } from '@/components/ui/button';
import { authService } from '../../services/authService';

const getPasswordRequirements = (password: string) => ({
  minLength: password.length >= 8,
  hasUpperCase: /[A-Z]/.test(password),
  hasLowerCase: /[a-z]/.test(password),
  hasNumber: /[0-9]/.test(password),
  hasSpecialChar: /[^A-Za-z0-9]/.test(password),
});

const PasswordResetForm: React.FC = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const email = searchParams.get('email') || '';
  
  const [code, setCode] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [newPasswordConfirm, setNewPasswordConfirm] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [showPasswordConfirm, setShowPasswordConfirm] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string>('');
  const [successMessage, setSuccessMessage] = useState<string>('');

  const requirements = getPasswordRequirements(newPassword);
  const allRequirementsMet =
    requirements.minLength &&
    requirements.hasUpperCase &&
    requirements.hasLowerCase &&
    requirements.hasNumber &&
    requirements.hasSpecialChar;

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsLoading(true);
    setError('');
    setSuccessMessage('');

    if (!code) {
      setError('Por favor, digite o código de verificação.');
      setIsLoading(false);
      return;
    }

    if (!newPassword) {
      setError('Por favor, digite a nova senha.');
      setIsLoading(false);
      return;
    }

    if (!allRequirementsMet) {
      setError('A senha não atende a todos os requisitos. Verifique o checklist abaixo.');
      setIsLoading(false);
      return;
    }

    if (newPassword !== newPasswordConfirm) {
      setError('As senhas não coincidem.');
      setIsLoading(false);
      return;
    }

    if (!email) {
      setError('Email não encontrado. Por favor, solicite um novo código de reset.');
      setIsLoading(false);
      return;
    }

    try {
      await authService.resetPassword(email, code, newPassword, newPasswordConfirm);
      
      setSuccessMessage('Senha redefinida com sucesso! Redirecionando para login...');
      
      setTimeout(() => {
        navigate('/login?message=Senha redefinida com sucesso! Faça login com sua nova senha.');
      }, 2000);
    } catch (err) {
      if (err instanceof Error) {
        setError(err.message);
      } else {
        setError('Erro ao redefinir senha. Verifique o código e tente novamente.');
      }
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div className="w-full max-w-md mx-auto">
      <div className="bg-card rounded-lg shadow-xl p-8 border border-border">
        {/* Header */}
        <div className="text-center mb-8">
          <img
            src="/imgs/logos/EscriturIA-logo-horizontal.png"
            alt="EscriturIA Logo"
            className="h-16 mx-auto mb-4"
          />
          <h1 className="text-2xl font-bold text-secondary font-serif">
            Redefinir Senha
          </h1>
          <p className="text-brand-text-muted mt-2">
            Digite o código recebido por email e sua nova senha
          </p>
        </div>

        {/* Success Message */}
        {successMessage && (
          <div className="mb-6 p-4 bg-green-100 border border-green-200 rounded-lg">
            <p className="text-green-800 text-sm">{successMessage}</p>
          </div>
        )}

        {/* Error Message */}
        {error && (
          <div className="mb-6 p-4 bg-brand-error-bg border border-brand-error-border rounded-lg">
            <p className="text-brand-error-text text-sm">{error}</p>
          </div>
        )}

        {/* Info Message */}
        {email && (
          <div className="mb-6 p-4 bg-blue-50 border border-blue-200 rounded-lg flex items-start">
            <InformationCircleIcon className="h-5 w-5 text-blue-600 mr-2 mt-0.5 flex-shrink-0" />
            <p className="text-blue-800 text-sm">
              Código enviado para: <strong>{email}</strong>
            </p>
          </div>
        )}

        {/* Form */}
        <form onSubmit={handleSubmit} className="space-y-6">
          {/* Code Field */}
          <div>
            <label htmlFor="code" className="block text-sm font-medium text-brand-text-primary mb-2">
              Código de Verificação
            </label>
            <input
              type="text"
              id="code"
              value={code}
              onChange={(e) => setCode(e.target.value.replace(/\D/g, '').slice(0, 6))}
              className="w-full px-4 py-3 bg-background border border-border rounded-lg text-foreground placeholder:text-muted-foreground focus:outline-none focus:ring-2 focus:ring-secondary focus:border-transparent transition-all duration-200 text-center text-2xl tracking-widest"
              placeholder="000000"
              maxLength={6}
              disabled={isLoading}
            />
            <p className="mt-2 text-xs text-brand-text-muted">
              Digite o código de 6 dígitos recebido por email
            </p>
          </div>

          {/* New Password Field */}
          <div>
            <label htmlFor="newPassword" className="block text-sm font-medium text-brand-text-primary mb-2">
              Nova Senha
            </label>
            <div className="relative">
              <input
                type={showPassword ? 'text' : 'password'}
                id="newPassword"
                value={newPassword}
                onChange={(e) => setNewPassword(e.target.value)}
                className="w-full px-4 py-3 pr-12 bg-background border border-border rounded-lg text-foreground placeholder:text-muted-foreground focus:outline-none focus:ring-2 focus:ring-secondary focus:border-transparent transition-all duration-200"
                placeholder="Sua nova senha"
                disabled={isLoading}
              />
              <button
                type="button"
                onClick={() => setShowPassword(!showPassword)}
                className="absolute right-3 top-1/2 transform -translate-y-1/2 text-brand-text-muted hover:text-brand-text-primary transition-colors duration-200"
                disabled={isLoading}
              >
                {showPassword ? '👁️' : '👁️‍🗨️'}
              </button>
            </div>
            <div className="mt-3 space-y-1">
              <p className="text-xs font-medium text-brand-text-primary mb-2">Requisitos da senha:</p>
              <div className="space-y-1">
                <div className="flex items-center text-xs">
                  <span className={`mr-2 ${requirements.minLength ? 'text-green-500' : 'text-brand-text-muted'}`}>
                    {requirements.minLength ? '✓' : '○'}
                  </span>
                  <span className={requirements.minLength ? 'text-green-500' : 'text-brand-text-muted'}>
                    Mínimo de 8 caracteres
                  </span>
                </div>
                <div className="flex items-center text-xs">
                  <span className={`mr-2 ${requirements.hasUpperCase ? 'text-green-500' : 'text-brand-text-muted'}`}>
                    {requirements.hasUpperCase ? '✓' : '○'}
                  </span>
                  <span className={requirements.hasUpperCase ? 'text-green-500' : 'text-brand-text-muted'}>
                    Pelo menos uma letra maiúscula (A-Z)
                  </span>
                </div>
                <div className="flex items-center text-xs">
                  <span className={`mr-2 ${requirements.hasLowerCase ? 'text-green-500' : 'text-brand-text-muted'}`}>
                    {requirements.hasLowerCase ? '✓' : '○'}
                  </span>
                  <span className={requirements.hasLowerCase ? 'text-green-500' : 'text-brand-text-muted'}>
                    Pelo menos uma letra minúscula (a-z)
                  </span>
                </div>
                <div className="flex items-center text-xs">
                  <span className={`mr-2 ${requirements.hasNumber ? 'text-green-500' : 'text-brand-text-muted'}`}>
                    {requirements.hasNumber ? '✓' : '○'}
                  </span>
                  <span className={requirements.hasNumber ? 'text-green-500' : 'text-brand-text-muted'}>
                    Pelo menos um número (0-9)
                  </span>
                </div>
                <div className="flex items-center text-xs">
                  <span className={`mr-2 ${requirements.hasSpecialChar ? 'text-green-500' : 'text-brand-text-muted'}`}>
                    {requirements.hasSpecialChar ? '✓' : '○'}
                  </span>
                  <span className={requirements.hasSpecialChar ? 'text-green-500' : 'text-brand-text-muted'}>
                    Pelo menos um caractere especial (@, #, $, !, etc)
                  </span>
                </div>
              </div>
            </div>
          </div>

          {/* Confirm Password Field */}
          <div>
            <label htmlFor="newPasswordConfirm" className="block text-sm font-medium text-brand-text-primary mb-2">
              Confirmar Nova Senha
            </label>
            <div className="relative">
              <input
                type={showPasswordConfirm ? 'text' : 'password'}
                id="newPasswordConfirm"
                value={newPasswordConfirm}
                onChange={(e) => setNewPasswordConfirm(e.target.value)}
                className="w-full px-4 py-3 pr-12 bg-background border border-border rounded-lg text-foreground placeholder:text-muted-foreground focus:outline-none focus:ring-2 focus:ring-secondary focus:border-transparent transition-all duration-200"
                placeholder="Confirme sua nova senha"
                disabled={isLoading}
              />
              <button
                type="button"
                onClick={() => setShowPasswordConfirm(!showPasswordConfirm)}
                className="absolute right-3 top-1/2 transform -translate-y-1/2 text-brand-text-muted hover:text-brand-text-primary transition-colors duration-200"
                disabled={isLoading}
              >
                {showPasswordConfirm ? '👁️' : '👁️‍🗨️'}
              </button>
            </div>
          </div>

          {/* Submit Button */}
          <Button
            type="submit"
            variant="hero"
            size="lg"
            disabled={isLoading || !allRequirementsMet}
            className="w-full text-lg px-8 py-6"
          >
            {isLoading ? (
              <>
                <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-white mr-2"></div>
                Redefinindo...
              </>
            ) : (
              'Redefinir Senha'
            )}
          </Button>
        </form>

        {/* Footer */}
        <div className="mt-8 text-center">
          <p className="text-brand-text-muted text-sm">
            Lembrou sua senha?{' '}
            <button 
              type="button"
              onClick={() => navigate('/login')}
              className="text-brand-accent hover:text-brand-accent-hover font-semibold hover:underline transition-colors duration-200"
            >
              Fazer login
            </button>
          </p>
        </div>
      </div>
    </div>
  );
};

export default PasswordResetForm;

